import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@/app/layout/pipes/translate.pipe';
import { Router } from '@angular/router';
import { NewsComponent } from '../news/news.component';
import { EntrepriseOptionService } from '../../core/service/entrepriseOption.service';
import { EntrepriseOptionsEnum } from '../../core/models/enums/entrepriseOption.enum';
import { CommonModule } from '@angular/common';
import { UserService } from '../../core/service/user.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
    standalone: true,
    templateUrl: './dashboard.component.html',
    imports: [CommonModule, NewsComponent, ButtonModule, TranslatePipe, ProgressSpinnerModule]
})
export class DashboardComponent implements OnInit {
    private readonly INSTALL_BANNER_DISMISSED_KEY = 'installBannerDismissed';

    readonly #entrepriseOptionsService = inject(EntrepriseOptionService);
    readonly #userService = inject(UserService);
    readonly #entrepriseOptionsQuery = this.#entrepriseOptionsService.listEntrepriseOptions();
    readonly #userInfoQuery = this.#userService.getUserInfos();

    // Computed pour vérifier si l'utilisateur est chargé et authentifié
    isUserLoading = computed(() => {
        return this.#userInfoQuery.isLoading();
    });

    prenomUser = computed(() => {
        const userData = this.#userInfoQuery.data();
        return userData ? userData.prenom : '';
    });

    isUserAuthenticated = computed(() => {
        const userData = this.#userInfoQuery.data();
        const isError = this.#userInfoQuery.isError();
        // Si il y a une erreur, considérer comme non authentifié
        return !!userData && !isError;
    });

    // Computed pour savoir si on doit afficher le contenu
    shouldShowContent = computed(() => {
        return !this.isUserLoading() && this.isUserAuthenticated();
    });

    linkApplicationEnabled = computed(() => {
        let data = this.#entrepriseOptionsQuery.data() ?? [];
        let x = data.find((x: any) => x.key.toLowerCase() == EntrepriseOptionsEnum.LinkApplicationEnabled.toLowerCase())?.value ?? false;
        return x.toString().toLowerCase() === 'true';
    });
    newsEnabled = computed(() => {
        let data = this.#entrepriseOptionsQuery.data() ?? [];
        let x = data.find((x: any) => x.key.toLowerCase() == EntrepriseOptionsEnum.NewsEnabled.toLowerCase())?.value ?? false;
        return x.toString().toLowerCase() === 'true';
    });
    meetingsEnabled = computed(() => {
        let data = this.#entrepriseOptionsQuery.data() ?? [];
        let x = data.find((x: any) => x.key.toLowerCase() == EntrepriseOptionsEnum.RdvEnabled.toLowerCase())?.value ?? false;
        return x.toString().toLowerCase() === 'true';
    });

    constructor(private readonly router: Router) { }


    scrollTo(viewChild: HTMLElement) {
        viewChild.scrollIntoView({ behavior: 'smooth' });
    }

    goToTodolist() {
        this.router.navigate(['/app/taches'])
    }

    goToMeetings() {
        this.router.navigate(
            ['/app/meetings'])
    }

    goToFiles() {
        this.router.navigate(
            ['/app/documents'])
    }

    retryAuthentication() {
        // Force une nouvelle vérification de l'utilisateur
        window.location.reload();
    }

    showInstallBanner = signal(false);


    ngOnInit() {
        const isMobile = this.#isAndroidDevice() || this.#isIOSDevice();
        const wasDismissed = this.#wasInstallBannerDismissed();
        
        // Afficher la bannière seulement si c'est mobile ET que l'utilisateur ne l'a pas déjà fermée
        this.showInstallBanner.set(isMobile && !wasDismissed);
    }

    closeInstallBanner() {
        this.showInstallBanner.set(false);
        // Sauvegarder dans le localStorage que l'utilisateur a fermé la bannière
        this.#saveInstallBannerDismissed();
    }

    #wasInstallBannerDismissed(): boolean {
        try {
            const dismissed = localStorage.getItem(this.INSTALL_BANNER_DISMISSED_KEY);
            return dismissed === 'true';
        } catch (error) {
            console.warn('Impossible d\'accéder au localStorage pour vérifier l\'état de la bannière d\'installation:', error);
            return false;
        }
    }

    #saveInstallBannerDismissed(): void {
        try {
            localStorage.setItem(this.INSTALL_BANNER_DISMISSED_KEY, 'true');
        } catch (error) {
            console.warn('Impossible de sauvegarder l\'état de la bannière d\'installation:', error);
        }
    }

    isInStandaloneMode(): boolean {
      const nav = window.navigator as any;
      // iOS Safari standalone => PWA installée, en mode 'PWA'
      if ('standalone' in nav && nav.standalone) {
        return true;
      }
      // Android/Chrome or other supporting display-mode  => PWA installée, en mode 'PWA'
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }
      return false;
    }

    installMobileApp() {
      if (this.#isIOSDevice() && !this.isInStandaloneMode()) {
        const appStoreId = '6751954975';
        const schemeUrl = `itms-apps://apps.apple.com/app/id${appStoreId}`;
        const fallbackUrl = `https://apps.apple.com/app/id${appStoreId}`;

        window.location.href = schemeUrl;
        // En cas d'échec (si l'app App Store n'est pas disponible), rediriger vers le lien de secours après un délai
        setTimeout(() => {
          window.location.href = fallbackUrl;
        }, 1500);

      } else if (this.#isAndroidDevice() && !this.isInStandaloneMode()) {
        const pkg = 'com.google.android.googlequicksearchbox';
        const intentUrl = `intent://details?id=${pkg}#Intent;scheme=market;package=com.android.vending;S.browser_fallback_url=${encodeURIComponent(`https://play.google.com/store/apps/details?id=${pkg}`)};end`;
        window.location.href = intentUrl;

        // En cas d'échec, rediriger vers le Play Store après un délai
        setTimeout(() => {
          const fallbackUrl = `https://play.google.com/store/apps/details?id=${pkg}`;
          window.location.href = fallbackUrl;
        }, 1500);

      }
    }


    #getUserAgent(): string {
      return window.navigator.userAgent;
    }

    #isAndroidDevice(): boolean {
        const ua = this.#getUserAgent();
        return /Android/i.test(ua);
    }

    #isIOSDevice(): boolean {
      const ua = this.#getUserAgent();
      // window['MSStream'] instead of window.MSStream to avoid TS error
      return /iPad|iPhone|iPod/.test(ua) && !(window as any)['MSStream'];
    }
}
